// components/formDialog/SetSemesterDialog.tsx
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";

// ─── Schema ───────────────────────────────────────────────────────────────────

const getSchema = (maxSemester: number) =>
  z.object({
    current_semester: z.preprocess(
      (v) => Number(v),
      z
        .number()
        .int("Semester must be a whole number")
        .min(1, "Semester must be at least 1")
        .max(maxSemester, `Semester cannot exceed ${maxSemester}`)
    ),
  });

type FormType = { current_semester: number };

// ─── Props ────────────────────────────────────────────────────────────────────

type SetSemesterDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentSemester: number;
  maxSemester: number;
  batchName: string;
  onConfirm: (semester: number) => void;
  isPending: boolean;
  onCancel: () => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

const SetSemesterDialog = ({
  isOpen,
  onOpenChange,
  currentSemester,
  maxSemester,
  batchName,
  onConfirm,
  isPending,
  onCancel,
}: SetSemesterDialogProps) => {
  const formSchema = useMemo(() => getSchema(maxSemester), [maxSemester]);

  const form = useForm<FormType>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: { current_semester: currentSemester },
  });

  // Sync default when dialog opens
  useEffect(() => {
    if (isOpen) {
      form.reset({ current_semester: currentSemester });
    }
  }, [isOpen, currentSemester, form]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
  };

  const handleSubmit: SubmitHandler<FormType> = (values) => {
    onConfirm(values.current_semester);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-sm"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Set Semester</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">
          Set a specific semester for{" "}
          <span className="font-medium text-gray-800">{batchName}</span>.
          Currently on Semester{" "}
          <span className="font-semibold">{currentSemester}</span>.
        </p>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit as any)}
            className="space-y-4"
          >
            <InputField<FormType>
              name="current_semester"
              label="Semester Number"
              placeholder="e.g. 3"
              type="number"
              disabled={isPending}
              required
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Set Semester"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SetSemesterDialog;
